import {
  BID_LADDER,
  nextPossibleBids,
  nextSeat,
  type BidEntry,
  type BiddingState,
  type BidValue,
  type SeatIndex,
} from "@meksiko/shared-types";
import { GameEngineError } from "./errors.js";

export function createBiddingState(firstBidder: SeatIndex): BiddingState {
  return {
    history: [],
    activeSeats: [0, 1, 2],
    turn: firstBidder,
    highestBid: null,
  };
}

export function legalBids(bidding: BiddingState): BidValue[] {
  return nextPossibleBids(bidding.highestBid?.value ?? undefined);
}

export function canPass(bidding: BiddingState): boolean {
  // The very first action of the whole bidding phase may not be a pass.
  return bidding.history.length > 0;
}

function biddingComplete(bidding: BiddingState): boolean {
  return bidding.activeSeats.length === 1;
}

export interface BiddingResult {
  bidding: BiddingState;
  /** Populated once bidding concludes (exactly one active seat remains). */
  winner: SeatIndex | null;
  winningBid: BidValue | null;
}

function toResult(bidding: BiddingState): BiddingResult {
  if (biddingComplete(bidding)) {
    const winnerSeat = bidding.activeSeats[0]!;
    const winningEntry = [...bidding.history].reverse().find((e) => e.seat === winnerSeat && e.value !== null);
    return {
      bidding,
      winner: winnerSeat,
      winningBid: winningEntry?.value ?? null,
    };
  }
  return { bidding, winner: null, winningBid: null };
}

export function applyBid(bidding: BiddingState, seat: SeatIndex, value: BidValue): BiddingResult {
  if (bidding.turn !== seat) {
    throw new GameEngineError(`Not seat ${seat}'s turn to bid`, "NOT_YOUR_TURN");
  }
  if (!BID_LADDER.includes(value)) {
    throw new GameEngineError(`Invalid bid value ${value}`, "INVALID_BID");
  }
  if (!legalBids(bidding).includes(value)) {
    throw new GameEngineError(
      `Bid ${value} does not exceed the current highest bid`,
      "BID_TOO_LOW",
    );
  }

  const history = [...bidding.history, { seat, value } satisfies BidEntry];
  const highestBid: BidEntry = { seat, value };
  const turn = advanceTurn(bidding.activeSeats, seat);

  return toResult({ ...bidding, history, highestBid, turn });
}

export function applyPass(bidding: BiddingState, seat: SeatIndex): BiddingResult {
  if (bidding.turn !== seat) {
    throw new GameEngineError(`Not seat ${seat}'s turn to bid`, "NOT_YOUR_TURN");
  }
  if (!canPass(bidding)) {
    throw new GameEngineError("The first bidder must bid at least 5, not pass", "MUST_BID_FIRST");
  }

  const history = [...bidding.history, { seat, value: null } satisfies BidEntry];
  const activeSeats = bidding.activeSeats.filter((s) => s !== seat);
  const turn = advanceTurn(activeSeats, seat);

  return toResult({ ...bidding, history, activeSeats, turn });
}

function advanceTurn(activeSeats: SeatIndex[], from: SeatIndex): SeatIndex {
  let seat = nextSeat(from);
  // Skip seats that have already passed (not in activeSeats), bounded by 3 hops.
  for (let i = 0; i < 3; i++) {
    if (activeSeats.includes(seat)) return seat;
    seat = nextSeat(seat);
  }
  return seat;
}
